import React, { useState } from 'react';
import { CalendarIcon } from 'lucide-react';

import { Button } from 'shadcn/components/ui/Button';
import { Calendar } from 'shadcn/components/ui/Calendar';
import { Popover, PopoverContent, PopoverTrigger } from 'shadcn/components/ui/Popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shadcn/components/ui/Select';
import { cn } from 'shadcn/lib/utils';
import dayjs from 'utils/dayjsConfig';

/**
 * Delivery type options
 */
const DELIVERY_TYPE_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'DBI', label: 'DBI' },
    { value: 'CARRIER', label: 'Carrier' },
];

/**
 * Status filter options
 */
const STATUS_FILTER_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'critical', label: 'Critical' },
    { value: 'atRisk', label: 'At Risk' },
    { value: 'onTrack', label: 'On Track' },
];

/**
 * Milestone filter options
 */
const MILESTONE_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'labels', label: 'Labels' },
    { value: 'packing', label: 'Packing' },
    { value: 'loading', label: 'Loading' },
    { value: 'complete', label: 'Complete' },
];

/**
 * FilterControls
 *
 * Filter bar for the Fulfillment Lifecycle Dashboard.
 * Contains date picker, delivery type, status filter, and milestone selectors.
 *
 * @param {Object} props
 * @param {string} props.selectedDate - Currently selected date (YYYY-MM-DD)
 * @param {Function} props.onDateChange - Callback when date changes
 * @param {Object} props.filters - Current filter state
 * @param {string[]} props.filters.deliveryTypes - Selected delivery types
 * @param {string} props.filters.statusFilter - Selected status filter
 * @param {string} props.filters.milestone - Selected milestone filter
 * @param {Function} props.onFilterChange - Callback when any filter changes: (filterKey, value) => void
 */
function FilterControls({ filters, onDateChange, onFilterChange, selectedDate }) {
    const [calendarOpen, setCalendarOpen] = useState(false);

    // Parse selectedDate string to Date for the Calendar (use dayjs for consistency with dashboard init)
    const selectedDateObj = selectedDate ? dayjs(selectedDate).toDate() : dayjs().toDate();

    const formattedDate = selectedDateObj.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });

    const handleDateSelect = (date) => {
        if (date) {
            onDateChange(dayjs(date).format('YYYY-MM-DD'));
            setCalendarOpen(false);
        }
    };

    const handleTodayClick = () => {
        onDateChange(dayjs().format('YYYY-MM-DD'));
    };

    // Derive single delivery type value for the select
    const deliveryTypeValue =
        filters.deliveryTypes && filters.deliveryTypes.length === 1 ? filters.deliveryTypes[0] : 'all';

    const handleDeliveryTypeChange = (value) => {
        onFilterChange('deliveryTypes', value === 'all' ? [] : [value]);
    };

    return (
        <div className="flex flex-wrap items-end gap-3">
            {/* Operating Date */}
            <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Operating Date</label>
                <div className="flex gap-1.5">
                    <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                className={cn(
                                    'w-[220px] justify-start text-left font-normal',
                                    !selectedDate && 'text-muted-foreground',
                                )}
                            >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {formattedDate}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                                mode="single"
                                selected={selectedDateObj}
                                onSelect={handleDateSelect}
                                initialFocus
                            />
                        </PopoverContent>
                    </Popover>
                    <Button variant="outline" size="sm" onClick={handleTodayClick}>
                        Today
                    </Button>
                </div>
            </div>

            {/* Delivery Type */}
            <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Delivery Type</label>
                <Select value={deliveryTypeValue} onValueChange={handleDeliveryTypeChange}>
                    <SelectTrigger className="w-[140px]">
                        <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                        {DELIVERY_TYPE_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Status Filter */}
            <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Status Filter</label>
                <Select
                    value={filters.statusFilter || 'all'}
                    onValueChange={(value) => onFilterChange('statusFilter', value)}
                >
                    <SelectTrigger className="w-[140px]">
                        <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                        {STATUS_FILTER_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Milestone */}
            <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Milestone</label>
                <Select
                    value={filters.milestone || 'all'}
                    onValueChange={(value) => onFilterChange('milestone', value)}
                >
                    <SelectTrigger className="w-[140px]">
                        <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                        {MILESTONE_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
        </div>
    );
}

export default FilterControls;
